/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { FC, useMemo } from 'react'
import { Form } from 'react-final-form'
import { MenuSelectField } from 'components/Select/MenuSelectField'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'

import { findAllRecommendedArticle_findAllRecommendedArticle_items as RecommendedArticle } from '__generatedTypes__/findAllRecommendedArticle'
import { findAllRecommendedArticleGroup_findAllRecommendedArticleGroup_items as Group } from '__generatedTypes__/findAllRecommendedArticleGroup'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { InputField } from 'components/Input'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import client from 'configs/apolloClient'
import { validateRequired } from 'utils/validation'
import { FieldValues } from 'utils/types'
import SelectMembershipType from 'components/SelectMembershipType'
import * as classes from 'components/shared/GroupCards/commonStyles'
import {
  CREATE_RECOMMENDED_ARTICLE,
  DELETE_RECOMMENDED_ARTICLE,
  FIND_ALL_RECOMMENDED_ARTICLES,
  UPDATE_RECOMMENDED_ARTICLE,
} from 'queries/recommendedArticle'
import {
  removeRecommendedArticle,
  removeRecommendedArticleVariables,
} from '__generatedTypes__/removeRecommendedArticle'
import {
  UpdateRecommendedArticle,
  UpdateRecommendedArticleVariables,
} from '__generatedTypes__/UpdateRecommendedArticle'
import {
  createRecommendedArticle,
  createRecommendedArticleVariables,
} from '__generatedTypes__/createRecommendedArticle'
import { toast } from 'utils/toast'

type Props = {
  recommendedArticle:
    | RecommendedArticle
    | { id: string; recommendedArticleGroup: { id: string }; membershipLevel?: { id: string } }
  onClose: () => void
  recommendedArticleGroups: Group[]
  newGroupOrder: number
  isEditMode: boolean
}

export const ManageRecommendedArticleModal: FC<Props> = ({
  recommendedArticle,
  onClose,
  recommendedArticleGroups,
  newGroupOrder,
  isEditMode,
}) => {
  const [updateRecommendedArticle, { loading: updateLoading }] = useMutation<
    UpdateRecommendedArticle,
    UpdateRecommendedArticleVariables
  >(UPDATE_RECOMMENDED_ARTICLE)
  const [removeRecommendedArticle] = useMutation<removeRecommendedArticle, removeRecommendedArticleVariables>(
    DELETE_RECOMMENDED_ARTICLE
  )

  const handleCloseAndUpdate = () => {
    onClose()
    client.refetchQueries({
      include: [FIND_ALL_RECOMMENDED_ARTICLES],
    })
  }

  const [createRecommendedArticle, { loading: createLoading }] = useMutation<
    createRecommendedArticle,
    createRecommendedArticleVariables
  >(CREATE_RECOMMENDED_ARTICLE)

  const handleGroupRemove = async () => {
    if (!recommendedArticle?.id) return

    try {
      await removeRecommendedArticle({ variables: { removeRecommendedArticleId: recommendedArticle.id } }).then(
        handleCloseAndUpdate
      )
      toast.success('Card deleted successfully')
    } catch (error) {
      toast.error('Failed to delete card')
    }
  }

  const handleSubmit = async (data: FieldValues) => {
    if (isEditMode) {
      try {
        await updateRecommendedArticle({
          variables: {
            params: {
              id: data.id,
              name: data.name,
              description: data.description || '',
              link: data.link,
              membershipLevel: data.membershipLevel.id,
              recommendedArticleGroup: {
                id: data.recommendedArticleGroup.id,
              },
            },
          },
          refetchQueries: () => [
            {
              query: FIND_ALL_RECOMMENDED_ARTICLES,
              variables: {
                query: {
                  take: TAKE_ALL_ITEMS_COUNT,
                  where: {
                    recommendedArticleGroup: {
                      id: data.recommendedArticleGroup.id,
                    },
                  },
                },
              },
            },
          ],
        })
        handleCloseAndUpdate()

        toast.success('Card updated successfully')
      } catch (error) {
        toast.error('Failed to update card')
      }
      //TODO Add error handling
    } else {
      try {
        await createRecommendedArticle({
          variables: {
            params: {
              name: data.name,
              description: data.description || '',
              order: newGroupOrder,
              link: data.link,
              membershipLevel: data.membershipLevel.id,
              recommendedArticleGroup: {
                id: data.recommendedArticleGroup.id,
              },
            },
          },
        })
        handleCloseAndUpdate()

        toast.success('Card created successfully')
      } catch (error) {
        toast.error('Failed to create card')
      }
      //TODO Add error handling
    }
  }

  const initialValues = useMemo(
    () =>
      isEditMode
        ? {
            ...recommendedArticle,
            membershipLevel: {
              id: recommendedArticle.membershipLevel,
            },
          }
        : recommendedArticle,
    [recommendedArticle]
  )

  return (
    <div css={classes.modalContainer}>
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, values }) => (
          <form css={classes.modalForm} onSubmit={handleSubmit}>
            <Typography css={classes.modalTitle} variant="title2">
              {isEditMode ? 'Edit Card' : 'Create Card'}
            </Typography>
            <InputField name="name" validate={validateRequired} label="Card Title" fullWidth />
            <TextareaAutosize
              value={values.description}
              label="Description"
              css={classes.descriptionInput}
              name="description"
            />
            <Typography variant="body2" css={classes.label}>
              Group
            </Typography>
            <MenuSelectField css={classes.select} fullWidth name="recommendedArticleGroup.id" required>
              {recommendedArticleGroups.map((group) => (
                <MenuItem value={group.id} key={group.id}>
                  <div>{group.title}</div>
                </MenuItem>
              ))}
            </MenuSelectField>
            <TextareaAutosize
              label="URL"
              validate={validateRequired}
              css={classes.descriptionInput}
              name="link"
              value={values.link}
            />
            <SelectMembershipType />
            <div css={classes.buttons}>
              {isEditMode && (
                <Button css={classes.button} variant="outlined" color="red" size="large" onClick={handleGroupRemove}>
                  <TrashIcon />
                  Delete
                </Button>
              )}
              <Button css={classes.button} variant="outlined" color="blue" size="large" onClick={onClose}>
                Cancel
              </Button>
              <Button
                css={[classes.button, isEditMode && classes.fullButton]}
                variant="contained"
                type="submit"
                color="blue"
                size="large"
              >
                {/*TODO Change loading after Nazrin`s answer*/}
                {createLoading || updateLoading ? '...Loading' : 'Submit'}
              </Button>
            </div>
          </form>
        )}
      />
    </div>
  )
}
